/**
 * Formats a number as currency based on the specified locale.
 * @param amount - The amount to format.
 * @param locale - The locale to use for formatting (e.g., 'en-US').
 * @returns The formatted currency string.
 */
export function formatCurrencyByLocale(amount?: number | null | undefined, locale?: string | null | undefined): string {
  return new Intl.NumberFormat(locale ?? 'en-US', {
    style: 'currency',
    currency: getCurrencyCodeByLocale(locale ?? 'en-US'),
  }).format(amount ?? 0);
}

/**
 * Formats a number as currency based on the specified currency code.
 * @param amount - The amount to format.
 * @param currencyCode - The currency code (e.g., 'USD').
 * @returns The formatted currency string.
 */
export function formatCurrencyByCode(amount?: number | null | undefined, currencyCode?: string | null | undefined): string {
  return new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: currencyCode ?? 'USD',
  }).format(amount ?? 0);
}

/**
 * Gets the currency code based on the locale.
 * @param locale - The locale to get the currency code for.
 * @returns The currency code (e.g., 'USD').
 */
export function getCurrencyCodeByLocale(locale: string): string {
  const currencyMap: { [key: string]: string } = {
    'en-US': 'USD',
    'en-GB': 'GBP',
    'de-DE': 'EUR',
    'fr-FR': 'EUR',
    'ja-JP': 'JPY',
    'zh-CN': 'CNY',
    'id-ID': 'IDR',
    // Add more locales and their corresponding currency codes as needed
  };

  return currencyMap[locale] || 'USD'; // Default to USD if locale not found
}

// Example usage:
// console.log(formatCurrencyByLocale(100000, 'en-US')); // "$100,000.00"
// console.log(formatCurrencyByCode(100000, 'USD')); // "$100,000.00"
// console.log(formatCurrencyByLocale(100000, 'de-DE')); // "100.000,00 €"
// console.log(formatCurrencyByCode(100000, 'EUR')); // "€100,000.00"